import {
  Box,
  BoxProps,
  CardMedia,
  Fade,
  Skeleton,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'

import { MembershipInterval } from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'
import { Organism } from '@/core/architecture'

export type DigitalMembershipCardData = {
  // カードデザイン画像URL
  cardDesignPath?: string
  // テキストカラー
  textColor?: string
  // 情報表示エリアカラー
  displayAreaColor?: string
  // ユーザ名
  displayName?: string
  // 会員番号
  membershipNumber?: string
  // サブスク更新期間
  planInterval?: MembershipInterval
  // 入会日
  joinDate?: string
  // 推し開始日
  favoriteMemberCreatedAt?: string
}

export type DigitalMembershipCardProps = DigitalMembershipCardData & {
  loading?: boolean
  onClick?: () => void
  displayAreaSx?: BoxProps['sx']
}

/**
 * @description
 * デジタル会員証カード表示コンポーネント
 */
export const DigitalMembershipCard: Organism<DigitalMembershipCardProps> = ({
  cardDesignPath,
  textColor,
  displayAreaColor,
  displayName,
  membershipNumber,
  planInterval,
  joinDate,
  favoriteMemberCreatedAt,
  loading = false,
  displayAreaSx,
  onClick,
}) => {
  const { t } = useTranslation('digitalMembershipCard')

  // 共通スタイル定義
  const typographyStyles = {
    displayName: {
      color: textColor || '#FFFFFF',
      fontSize: {
        xs: '13px',
        sm: '16px',
      },
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '20px',
      textAlign: 'left',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
    },
    label: {
      color: textColor || '#FFFFFF',
      fontSize: {
        xs: '9px',
        sm: '14px',
      },
      whiteSpace: 'nowrap',
      fontWeight: 300,
      flexShrink: 0,
    },
    value: {
      color: textColor || '#FFFFFF',
      fontSize: {
        xs: '10px',
        sm: '14px',
      },
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: 1,
    },
  }

  if (loading) {
    return (
      <Box sx={{ px: 5, width: '100%' }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={280}
          sx={{ borderRadius: 3 }}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Fade in>
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 3,
            position: 'relative',
            width: '100%',
            aspectRatio: '856/540',
            mx: 'auto',
            backgroundImage: `
              linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
              linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
              linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
            `,
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={onClick}
        >
          {cardDesignPath && (
            <CardMedia
              component="img"
              image={cardDesignPath}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 3,
              }}
            />
          )}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              height: '32.2%',
              backgroundColor: displayAreaColor || 'transparent',
              borderRadius: '0 0 12px 12px',
              padding: '8px 0',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              minHeight: 0,
              ...displayAreaSx,
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                padding: '0 12px',
                minHeight: 0,
              }}
            >
              <Typography variant="h6" sx={typographyStyles.displayName}>
                {displayName} {t('sama')}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                gap: '8px',
                padding: '0 12px',
                minHeight: 0,
              }}
            >
              {membershipNumber && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1,
                    paddingLeft: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Typography variant="caption" sx={typographyStyles.label}>
                    {t('membershipNumber')}
                  </Typography>
                  <Typography variant="body2" sx={typographyStyles.value}>
                    {membershipNumber}
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1,
                  paddingLeft: 1,
                  overflow: 'hidden',
                }}
              >
                <Typography variant="caption" sx={typographyStyles.label}>
                  {t('plan')}
                </Typography>
                <Typography variant="body2" sx={typographyStyles.value}>
                  {planInterval
                    ? planInterval === MembershipInterval.Year
                      ? t('yearly')
                      : t('monthly')
                    : ''}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                gap: '8px',
                padding: '0 12px',
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1,
                  paddingLeft: 1,
                  overflow: 'hidden',
                }}
              >
                <Typography variant="caption" sx={typographyStyles.label}>
                  {t('joinDate')}
                </Typography>
                <Typography variant="body2" sx={typographyStyles.value}>
                  {joinDate
                    ? new Date(joinDate).toLocaleDateString('ja-JP')
                    : ''}
                </Typography>
              </Box>

              {favoriteMemberCreatedAt && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1,
                    paddingLeft: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Typography variant="caption" sx={typographyStyles.label}>
                    {t('fanHistory')}
                  </Typography>
                  <Typography variant="body2" sx={typographyStyles.value}>
                    {favoriteMemberCreatedAt && joinDate
                      ? (() => {
                          const favoriteDate = dayjs(
                            favoriteMemberCreatedAt
                          ).startOf('day')
                          const now = dayjs().startOf('day')
                          const days = now.diff(favoriteDate, 'day')
                          return days >= 0 ? `${days}${t('days')}` : ''
                        })()
                      : ''}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>
    </Box>
  )
}
